import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, ChevronDown } from "lucide-react";
import { navLinks, company } from "../mock";

const Logo = () => (
  <Link to="/" className="flex items-center gap-2.5 group">
    <img
      src={company.logo}
      alt="Fab Build Pro logo"
      className="h-16 w-auto object-contain group-hover:scale-105 transition-transform"
    />
  </Link>
);

const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      className={`sticky top-0 z-50 bg-brand-cream/95 backdrop-blur border-b transition-shadow ${
        scrolled ? "shadow-md border-transparent" : "border-black/5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 h-[72px] flex items-center justify-between">
        <Logo />

        <nav className="hidden lg:flex items-center gap-1">
          {navLinks.map((l) => {
            const active = pathname === l.to;
            if (l.children) {
              return (
                <div key={l.label} className="relative group">
                  <Link
                    to={l.to}
                    className={`flex items-center gap-1 px-3.5 py-2 rounded-lg text-[15px] font-medium transition-colors ${
                      active ? "text-brand-orange" : "text-brand-navy hover:text-brand-orange"
                    }`}
                  >
                    {l.label}
                    <ChevronDown className="w-4 h-4 group-hover:rotate-180 transition-transform" />
                  </Link>
                  <div className="absolute left-0 top-full pt-2 w-60 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                    <div className="bg-white rounded-xl shadow-xl border border-black/5 py-2">
                      {l.children.map((c) => (
                        <Link
                          key={c.label}
                          to={c.to}
                          className="block px-4 py-2.5 text-[14px] text-brand-navy hover:bg-brand-cream hover:text-brand-orange transition-colors"
                        >
                          {c.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              );
            }
            return (
              <Link
                key={l.to}
                to={l.to}
                className={`px-3.5 py-2 rounded-lg text-[15px] font-medium transition-colors ${
                  active
                    ? "text-brand-orange"
                    : "text-brand-navy hover:text-brand-orange"
                }`}
              >
                {l.label}
              </Link>
            );
          })}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href={company.phoneHref}
            className="flex items-center gap-2 text-brand-navy font-semibold hover:text-brand-orange transition-colors"
          >
            <Phone className="w-4 h-4" />
            {company.phone}
          </a>
          <Link
            to="/contact"
            onClick={() => window.gtag_report_conversion && window.gtag_report_conversion()}
            data-testid="header-get-estimate"
            className="bg-brand-orange hover:bg-brand-orangedark text-white font-semibold px-5 py-2.5 rounded-full shadow-md hover:shadow-lg transition-all hover:-translate-y-0.5"
          >
            Get Free Estimate
          </Link>
        </div>

        <button
          className="lg:hidden p-2 text-brand-navy"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-brand-cream border-t border-black/5 px-6 py-4 space-y-1 animate-fade-up">
          {navLinks.map((l) => (
            <div key={l.label}>
              <Link
                to={l.to}
                className="block py-2.5 text-brand-navy font-medium border-b border-black/5"
              >
                {l.label}
              </Link>
              {l.children && (
                <div className="pl-4 py-1 space-y-1">
                  {l.children.map((c) => (
                    <Link
                      key={c.label}
                      to={c.to}
                      className="block py-1.5 text-sm text-slate-600 hover:text-brand-orange"
                    >
                      {c.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
          <div className="pt-4 flex flex-col gap-3">
            <a
              href={company.phoneHref}
              className="flex items-center gap-2 text-brand-navy font-semibold"
            >
              <Phone className="w-4 h-4" /> {company.phone}
            </a>
            <Link
              to="/contact"
              onClick={() => window.gtag_report_conversion && window.gtag_report_conversion()}
              className="bg-brand-orange text-white font-semibold px-5 py-3 rounded-full text-center"
            >
              Get Free Estimate
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
